"use client";

import * as Toast from "@radix-ui/react-toast";
import clsx from "clsx";
import { useState } from "react";
import styles from "./Notification.module.scss";
import { useI18n } from "@/i18n/client";

import WarningIcon from "@/app/assets/images/svg/alert-triangle.svg";
import SuccessIcon from "@/app/assets/images/svg/check-circle.svg";
import InfoIcon from "@/app/assets/images/svg/info.svg";
import ErrorIcon from "@/app/assets/images/svg/x-octagon.svg";
import SaleIcon from "@/app/assets/images/svg/tag-solid-full.svg";

export type NotificationVariant =
	| "info"
	| "success"
	| "warning"
	| "error"
	| string;
export type ToastPosition = "bottom-left" | "bottom-right" | "top-right";
interface Props {
	type?: "notification" | "toast";
	variant: NotificationVariant;
	title?: string;
	children?: React.ReactNode;
	toastAutoClose?: boolean;
	showClose?: boolean;
	position?: ToastPosition;
	customIcon?: NotificationVariant;
	toastDuration?: number;
}

const Notification = ({
	type = "notification",
	title,
	showClose = true,
	position = "bottom-right",
	customIcon,
	...props
}: Props) => {
	const t = useI18n();
	const variantToIcon: Record<NotificationVariant, React.ReactNode> = {
		info: <InfoIcon />,
		success: <SuccessIcon />,
		warning: <WarningIcon />,
		error: <ErrorIcon />,
		sale: <SaleIcon />,
	};

	const iconVariant = customIcon || props.variant;
	const getIconFromVariant = (iconVariant: NotificationVariant) => {
		return variantToIcon[iconVariant];
	};

	const getIcon = () => {
		switch (props.variant) {
			case "info":
				return <InfoIcon />;
			case "success":
				return <SuccessIcon />;
			case "warning":
				return <WarningIcon />;
			case "error":
				return <ErrorIcon />;
			case "sale":
				return <SaleIcon />;
		}
	};

	const content = (
		<div className={styles.container}>
			{getIconFromVariant(iconVariant)}
			<div className={styles.content}>
				{title && <div className={styles.title}>{title}</div>}
				{props.children}
			</div>
		</div>
	);

	if (type === "toast") {
		return (
			<ToastNotification {...props} position={position}>
				{content}
			</ToastNotification>
		);
	}

	return (
		<div className={clsx([styles.notification, styles[props.variant]])}>
			{content}
		</div>
	);
};

const ToastNotification = ({
	variant,
	children,
	toastAutoClose,
	toastDuration,
	showClose,
	position = "bottom-right",
}: Props) => {
	const [open, setOpen] = useState(true);
	const t = useI18n();

	// If toastAutoClose is true but no duration is provided, use a default duration
	const duration = toastAutoClose && !toastDuration ? 5000 : toastDuration;

	return (
		<Toast.Provider swipeDirection='right'>
			<Toast.Root
				className={clsx([styles.toastRoot, styles[variant]])}
				open={open}
				onOpenChange={setOpen}
				duration={duration}
			>
				<Toast.Description asChild>
					<>
						{children}
						{showClose ? (
							<button
								type='button'
								aria-label={t("Notification.closeNotification")}
								className={styles.closeToast}
								onClick={() => setOpen(false)}
							>
								Close
							</button>
						) : null}
					</>
				</Toast.Description>
			</Toast.Root>
			<Toast.Viewport
				className={clsx("ToastViewport", styles[`viewport-${position}`])}
			/>
		</Toast.Provider>
	);
};

export default Notification;
